<script setup lang="ts">
import { computed } from 'vue'
import { DateTime } from 'luxon'
import VehicleTypeIcon from '@/components/VehicleTypeIcon.vue'
import {
  currencyFilter,
  pluralize,
  toTitle,
} from '@/utils/string'
import { EventBus } from '@/utils/eventBus'
import ReservationSummary from '@/models/dto/reservationSummary/ReservationSummary'
import { ReservationStatusKey } from '@/utils/enum'

interface Props {
  row: ReservationSummary
  uuid: string
  selectedReservationIds: number[]
}

const props = withDefaults(defineProps<Props>(), {
  row: null,
})

const emit = defineEmits<{
  click: []
}>()

const selected = computed(() => props.selectedReservationIds.includes(props.row.id))

const tripType = computed(() => toTitle(props.row.tripType))

const reservationStatus = computed(() => {
  const tripStatus = props.row.tripStatus === ReservationStatusKey.OnHold
    ? ReservationStatusKey.Upcoming
    : props.row.tripStatus
  return toTitle(tripStatus)
})

const vehicles = computed(() => {
  const vehicles = props.row.vehicles
  if (!vehicles) {
    return ''
  }
  if (vehicles.length > 1) {
    const totalVehicleCount = vehicles.reduce(
      (sum, vehicle) => sum + vehicle.quantity,
      0
    )
    return `${totalVehicleCount} Vehicles`
  }
  const quantity = vehicles[0].quantity
  const label = vehicles[0].label
  return `${quantity} ${pluralize(quantity, label)}`
})

const passengerCount = computed(() => props.row.passengerCount || null)

const pickupDate = computed(() => {
  const pickupDatetime = props.row.pickupDatetime
  const pickupTimezone = props.row.pickupTimezone
  if (!pickupDatetime || !pickupTimezone) {
    return ''
  }
  return DateTime.fromISO(pickupDatetime, { zone: pickupTimezone }).toFormat('MM/dd/yy')
})

const pickupTime = computed(() => {
  const pickupDatetime = props.row.pickupDatetime
  const pickupTimezone = props.row.pickupTimezone
  if (!pickupDatetime || !pickupTimezone) {
    return ''
  }
  return DateTime.fromISO(pickupDatetime, { zone: pickupTimezone }).toFormat('h:mm a')
})

const displayPrice = computed(() => {
  if (!props.row.price) {
    return ''
  }
  return currencyFilter(props.row.price)
})

const paymentStatus = computed(() => toTitle(props.row.paymentStatus))

const displayVehicleType = computed(() => props.row.vehicles?.[0]?.label || null)

const handleSelectClick = (value: boolean): void => {
  EventBus.$emit(`toggle-select-item-${props.uuid}`, {
    key: props.row.id,
    value,
  })
}
</script>

<template>
  <v-card class="padding-a-4 margin-b-2" @click="$emit('click')">
    <div class="d-flex justify-space-between align-center">
      <span class="margin-r-3 font-bold">{{ row.routeName }}</span>
      <div class="d-flex align-center flex-row">
        <v-checkbox
          :input-value="selected"
          hide-details
          class="margin-t-0 padding-t-0"
          @click.stop
          @change="handleSelectClick($event)"
        />
        <CUIcon view-box="0 0 24 24" color="primary">
          keyboard_arrow_right
        </CUIcon>
      </div>
    </div>

    <div class="d-flex justify-space-between">
      <div class="d-flex flex-column">
        <div>
          <span class="text-gray-mid-light margin-r-1">ID {{ row.managedId }}</span>
          {{ tripType }}
        </div>
        <div class="d-flex align-center">
          {{ vehicles }}
          <CUIcon view-box="0 0 24 24" color="gray-light" class="margin-x-1">
            person
          </CUIcon>
          {{ passengerCount }}
        </div>
        <div class="flex-grow-1" />
        <VehicleTypeIcon
          v-if="displayVehicleType"
          :vehicle-type-label="displayVehicleType"
          width="100"
          height="60"
        />
      </div>
      <div class="d-flex flex-column text-right">
        <div>
          {{ reservationStatus }}
        </div>
        <div>
          {{ pickupDate }}
        </div>
        <div>
          {{ pickupTime }}
        </div>
        <div class="font-bold">
          {{ displayPrice }}
        </div>
        <div>
          {{ paymentStatus }}
        </div>
      </div>
    </div>
  </v-card>
</template>
